import { collectionOptions } from "@tanstack/db";
import { hashKey, type QueryClient } from "@tanstack/query-core";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { apiDelete, apiGet } from "@softmax/observatory-client/client";
import { ApiError } from "@softmax/observatory-client/transport";
import type { WikiPagePublic } from "@softmax/observatory-client/types";
import { z } from "zod";
import {
  wikiIndexCollection,
  wikiReadOptions,
  type WikiIdentity,
} from "./wiki.collections";

const deletionMetadata = z.object({ reason: z.string() });

export function wikiPageCollection({
  wikiSlug,
  pageSlug,
  elevatedPrivileges,
  initialPage,
  initialMissing = false,
  initialDataUpdatedAt,
}: WikiIdentity & {
  initialPage?: WikiPagePublic | null | undefined;
  initialMissing?: boolean | undefined;
  initialDataUpdatedAt?: number | undefined;
}) {
  const queryKey = [
    "wikis",
    wikiSlug,
    "pages",
    pageSlug,
    { elevatedPrivileges },
  ] as const;
  let initialData: WikiPagePublic[] | undefined;

  if (initialPage) initialData = [initialPage];
  else if (initialMissing) initialData = [];

  return collectionOptions(hashKey(queryKey), (db) => {
    const queryClient = db.requireDependency<QueryClient>("queryClient");
    return queryCollectionOptions<WikiPagePublic>({
      queryClient,
      queryKey,
      queryFn: ({ signal }) =>
        apiGet<WikiPagePublic>(
          `/v2/wikis/${encodeURIComponent(wikiSlug)}/pages/${pageSlug.split("/").map(encodeURIComponent).join("/")}`,
          { signal, elevatedPrivileges },
        ).then(
          (page) => [page],
          (error: unknown) => {
            if (error instanceof ApiError && error.status === 404) return [];

            throw error;
          },
        ),
      getKey: (page) => page.id,
      initialData,
      initialDataUpdatedAt: initialPage ? initialDataUpdatedAt : 0,
      ...wikiReadOptions,
      onDelete: async ({ transaction, collection }) => {
        const signal = db.requireDependency<AbortController>("lifetime").signal;
        signal.throwIfAborted();
        const index = db.collection(
          wikiIndexCollection({ wikiSlug, elevatedPrivileges }),
        );

        for (const mutation of transaction.mutations) {
          const { reason } = deletionMetadata.parse(mutation.metadata);
          await apiDelete<void>(
            `/v2/wiki-pages/${encodeURIComponent(String(mutation.key))}`,
            { reason },
            { signal, elevatedPrivileges },
          ).catch((error: Error) => {
            db.requireDependency<(error: Error) => void>(
              "reportAuthorizationError",
            )(error);
            throw error;
          });
          signal.throwIfAborted();
          await Promise.all([
            queryClient.cancelQueries({
              queryKey: ["wikis", wikiSlug, "index"],
            }),
            queryClient.cancelQueries({
              queryKey: ["wikis", wikiSlug, "pages", pageSlug],
            }),
          ]);
          signal.throwIfAborted();
          await collection.utils.writeDelete(mutation.key);

          if (index.has(mutation.key))
            await index.utils.writeDelete(mutation.key);
        }

        const refreshes: Promise<unknown>[] = [
          queryClient.invalidateQueries({
            queryKey: [
              "wikis",
              wikiSlug,
              "index",
              { elevatedPrivileges: !elevatedPrivileges },
            ],
          }),
          queryClient.invalidateQueries({
            queryKey: [
              "wikis",
              wikiSlug,
              "pages",
              pageSlug,
              { elevatedPrivileges: !elevatedPrivileges },
            ],
          }),
        ];

        if (index.status === "loading") refreshes.push(index.utils.refetch());

        await Promise.allSettled(refreshes);
      },
    });
  });
}
